import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import { colors } from '../../styles/theme';
import {
  BlockHeader,
  FORM_GROUP_TITLE_SX,
  FORM_PRIMARY_BUTTON_SX,
  FORM_SECONDARY_BUTTON_SX,
  PUBLICATION_BLOCK_SX,
} from '../../Map/components/DockPanel/Tabs/formBlockUi';
import ShowroomManageFilters from './ShowroomManageFilters';
import ShowroomParcelListEmpty from './ShowroomParcelListEmpty';
import ShowroomParcelPlanimetria from './ShowroomParcelPlanimetria';
import CadParcelImages from './CadParcelImages';
import CadParcelVideos from './CadParcelVideos';
import CadParcelAttachments from './CadParcelAttachments';
import {
  CAD_KIND_DEFAULT,
  CAD_KIND_LABELS,
  CAD_KINDS,
  CAD_PARCEL_MAX_DESC,
  CAD_PARCEL_MAX_NAME,
  CAD_PARCEL_MAX_BLOCK,
  CAD_PARCEL_MAX_TITLE,
  CAD_STOCK_LABELS,
  CAD_STOCK_STATUSES,
  CAD_USES,
  CAD_USE_LABELS,
} from '../data/cadConstants';
import {
  ORIENTATION_EMPTY_LABEL,
  buildOrientationSelectOptions,
  getOrientationFormLabel,
} from '../../Utils/compassOrientation';
import { toast } from '../../Components/Toast';
import { useScrollEdgeFade, getScrollEdgeFadeSx } from '../../hooks/useScrollEdgeFade';
import {
  getCadParcelId,
  filterCadParcelsForManageList,
  normalizeCadStatusFilter,
  parseCadParcelAreaM2,
  sanitizeCadDecimalInput,
  sortCadParcels,
  buildCadParcelUpdateBody,
  formatCadAreaInputValue,
} from '../utils/cadParcelDisplay';
import { filterCadSellableLots, getCadKind, isCadSellableLot } from '../utils/cadMasterplan';
import {
  formatPriceInputDisplay,
  parsePriceInputValue,
} from '../../Map/utils/listingCoreConstants';
import CadParcelListMeta from './CadParcelListMeta';
import CadParcelListHeading from './CadParcelListHeading';
import CollectionInquiryBadge from '../../Components/CollectionInquiryBadge';
import StockStatusBadge from './StockStatusBadge';
import { getCollectionInquiryItemCounts } from '../../Map/utils/collectionInquiryStats';
import { MANAGE_LIST_SORT_OPTIONS } from '../../Map/utils/listSort';
import {
  SHOWROOM_PANEL_BG,
  SHOWROOM_PANEL_BORDER,
  showroomCardSx,
  showroomFieldSx,
  showroomFormFooterSx,
  showroomFormGroupDividerSx,
  showroomFormGroupTitleSx,
  showroomFormLabelSx,
  showroomMenuItemSx,
  showroomMenuPaperSx,
  showroomSelectSx,
  showroomToolbarSx,
  showroomScrollSx,
} from '../Showroom.styles';
import { marketRevisionOf } from '../../MarketComparison/marketComparison';

function FieldCharCount({ value, max }) {
  const n = String(value || '').length;
  return (
    <Typography
      variant="caption"
      sx={{
        color: n > max * 0.9 ? colors.error : colors.textSecondary,
        fontSize: '12px',
        float: 'right',
      }}
    >
      {n}/{max}
    </Typography>
  );
}

function emptyForm() {
  return {
    name: '',
    title: '',
    description: '',
    cad_kind: CAD_KIND_DEFAULT,
    use: '',
    stock_status: 'available',
    price: '',
    currency: 'USD',
    orientation: '',
    position_label: '',
    block: '',
    fos: '',
    fot: '',
    area_m2: '',
  };
}

function formFromFeature(feature) {
  const props = feature?.properties || {};
  return {
    name: props.name || '',
    title: props.title || '',
    description: props.description || '',
    cad_kind: getCadKind(feature),
    use: props.use || '',
    stock_status: props.stock_status || 'available',
    price: props.price == null ? '' : String(props.price),
    currency: props.currency || 'USD',
    orientation: props.orientation || '',
    position_label: props.position_label || '',
    block: props.block == null ? '' : String(props.block),
    fos: props.fos == null ? '' : String(props.fos),
    fot: props.fot == null ? '' : String(props.fot),
    area_m2: formatCadAreaInputValue(props.area_m2),
  };
}

export default function ShowroomManagePanel({
  features,
  selectedId,
  onSelect,
  onSave,
  onDelete,
  onPhotosChange,
  onVideosChange,
  onAttachmentsChange,
  saving,
  deleting = false,
  listingCoreOnly = false,
  statusFilter = null,
  onStatusFilterChange,
  parcelSearch: parcelSearchProp,
  onParcelSearchChange,
  inquiryByItem = null,
  /** R1 fase 3: "Consultas y leads" del lote abierto (la arma CadInListsDetail). Primer bloque, fuera de edición. */
  recordLeads = null,
  recordCampaigns = null,
  /** Comparación de mercado: `({ onEdit, revision }) => node` (la arma CadInListsDetail). */
  renderMarketComparison = null,
}) {
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(false);
  const [sortBy, setSortBy] = useState('name');
  const [parcelSearchInternal, setParcelSearchInternal] = useState('');
  const parcelSearch = onParcelSearchChange ? (parcelSearchProp || '') : parcelSearchInternal;
  const setParcelSearch = onParcelSearchChange || setParcelSearchInternal;
  const scrollNodeRef = useRef(null);
  const { scrollRef: fadeRef, fadeTop, fadeBottom } = useScrollEdgeFade();
  const setScrollNode = useCallback((node) => {
    scrollNodeRef.current = node;
    fadeRef(node);
  }, [fadeRef]);

  const selected = useMemo(
    () => features.find((feature) => getCadParcelId(feature) === selectedId) || null,
    [features, selectedId],
  );

  useEffect(() => {
    setForm(selected ? formFromFeature(selected) : emptyForm());
    setEditing(false);
    if (!selectedId) return;
    const el = scrollNodeRef.current;
    if (el) el.scrollTop = 0;
  }, [selected, selectedId]);

  const counts = useMemo(() => {
    const next = {};
    filterCadSellableLots(features).forEach((feature) => {
      const status = feature?.properties?.stock_status || 'available';
      next[status] = (next[status] || 0) + 1;
    });
    return next;
  }, [features]);

  const getInquiryTotal = useCallback((feature) => (
    getCollectionInquiryItemCounts(inquiryByItem, getCadParcelId(feature))?.total || 0
  ), [inquiryByItem]);

  // Solo lotes en el listado. Calle / verde / predio se abren desde el mapa
  // (siguen en `features` para resolver la ficha al click).
  const visible = useMemo(
    () => sortCadParcels(
      filterCadParcelsForManageList(features, {
        query: parcelSearch,
        statusFilter,
        includeContext: false,
      }),
      sortBy,
      { getInquiryTotal },
    ),
    [features, parcelSearch, statusFilter, sortBy, getInquiryTotal],
  );

  const handleChange = (key) => (event) => {
    setForm((prev) => ({ ...prev, [key]: event.target.value }));
  };

  const handleKindChange = (event) => {
    const cad_kind = event.target.value;
    setForm((prev) => {
      if (cad_kind === 'lot') return { ...prev, cad_kind };
      return { ...prev, cad_kind, use: 'amenity', stock_status: 'unavailable' };
    });
  };

  const handlePriceChange = (event) => {
    setForm((prev) => ({ ...prev, price: parsePriceInputValue(event.target.value) }));
  };

  const handleAreaChange = (event) => {
    setForm((prev) => ({ ...prev, area_m2: sanitizeCadDecimalInput(event.target.value) }));
  };

  const fieldsLocked = !editing || saving || deleting;
  const inventoryLocked = fieldsLocked || form.cad_kind !== 'lot';
  const selectedInquiryCounts = getCollectionInquiryItemCounts(inquiryByItem, selectedId);
  const canDeleteLot = Boolean(onDelete) && !listingCoreOnly;
  const showLotActions = !editing || canDeleteLot;

  const cancelEdit = () => {
    setForm(selected ? formFromFeature(selected) : emptyForm());
    setEditing(false);
  };

  const startEdit = () => setEditing(true);

  const handleDelete = () => {
    if (selectedId) onDelete?.(selectedId);
  };

  const headerActionIconSx = {
    color: colors.textPanelTitle,
    p: 0.5,
    '&:hover': { color: colors.textPrimary, bgcolor: 'rgba(255,255,255,0.06)' },
  };

  const handleSave = async () => {
    if (!selectedId) return;
    if (!listingCoreOnly) {
      const name = String(form.name || '').trim();
      if (!name) {
        toast.error('El lote no puede quedar vacío.');
        return;
      }
      if (name.length > CAD_PARCEL_MAX_NAME) {
        toast.error(`El lote admite hasta ${CAD_PARCEL_MAX_NAME} caracteres.`);
        return;
      }
    }
    const area = parseCadParcelAreaM2(form.area_m2);
    if (area.error) {
      toast.error(area.error);
      return;
    }
    const ok = await onSave(selectedId, buildCadParcelUpdateBody(form, { listingCoreOnly }));
    if (ok) setEditing(false);
  };

  const toolbar = !selected ? (
        <Box sx={showroomToolbarSx}>
          <ShowroomManageFilters
            searchQuery={parcelSearch}
            onSearchChange={setParcelSearch}
            statusFilter={statusFilter}
            onStatusChange={onStatusFilterChange}
            featuresCount={filterCadSellableLots(features).length}
            counts={counts}
            sortBy={sortBy}
            onSortChange={setSortBy}
            sortOptions={MANAGE_LIST_SORT_OPTIONS}
          />
        </Box>
      ) : null;

  const detailChrome = selected ? (
    <Box
      sx={{
        flexShrink: 0,
        px: 2.5,
        pt: 1.5,
        pb: 1.25,
        bgcolor: SHOWROOM_PANEL_BG,
        borderBottom: `1px solid ${SHOWROOM_PANEL_BORDER}`,
      }}
    >
      <BlockHeader
        icon={<PublicOutlinedIcon sx={{ fontSize: 16, color: colors.textSecondary, mt: 0.15 }} />}
        title="Publicación"
        subtitle={isCadSellableLot(selected)
          ? 'Datos comerciales del lote.'
          : 'Tipo en el plano y datos de publicación.'}
        action={(
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
            {selected && !editing ? (
              // En lectura el estado va como badge (el select solo en edición).
              <StockStatusBadge status={form.stock_status} />
            ) : null}
            {selectedInquiryCounts ? (
              <CollectionInquiryBadge
                unseen={selectedInquiryCounts.unseen}
                total={selectedInquiryCounts.total}
              />
            ) : null}
            {showLotActions ? (
              !editing ? (
                <Tooltip title="Editar" placement="top" arrow>
                  <IconButton
                    size="small"
                    aria-label="Editar"
                    onClick={startEdit}
                    disabled={saving || deleting}
                    sx={headerActionIconSx}
                  >
                    <EditOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : canDeleteLot ? (
                <Tooltip title="Eliminar de la lista" placement="top" arrow>
                  <IconButton
                    size="small"
                    aria-label="Eliminar de la lista"
                    onClick={handleDelete}
                    disabled={saving || deleting || !selectedId}
                    sx={{
                      ...headerActionIconSx,
                      '&:hover': {
                        color: colors.error,
                        bgcolor: 'rgba(255,255,255,0.06)',
                      },
                    }}
                  >
                    {deleting ? (
                      <CircularProgress size={16} sx={{ color: colors.textPanelTitle }} />
                    ) : (
                      <DeleteOutlineIcon fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
              ) : null
            ) : null}
          </Box>
        )}
      />
    </Box>
  ) : null;

  const body = selected ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1.5, pb: 1 }}>
            {!editing && isCadSellableLot(selected) ? recordLeads : null}
            {!editing && isCadSellableLot(selected) ? recordCampaigns : null}
            <Box sx={{ ...PUBLICATION_BLOCK_SX, p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Typography sx={FORM_GROUP_TITLE_SX}>Lote</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                  <Box>
                    <TextField
                      label="Manzana"
                      size="small"
                      fullWidth
                      variant="outlined"
                      value={form.block}
                      disabled={fieldsLocked}
                      inputProps={{ maxLength: CAD_PARCEL_MAX_BLOCK }}
                      onChange={handleChange('block')}
                      sx={showroomFieldSx}
                    />
                    {editing ? <FieldCharCount value={form.block} max={CAD_PARCEL_MAX_BLOCK} /> : null}
                  </Box>
                  <Box>
                    <TextField
                      label="Lote"
                      size="small"
                      fullWidth
                      variant="outlined"
                      value={form.name}
                      disabled={listingCoreOnly || fieldsLocked}
                      inputProps={{ maxLength: CAD_PARCEL_MAX_NAME }}
                      onChange={handleChange('name')}
                      sx={showroomFieldSx}
                    />
                    {editing && !listingCoreOnly
                      ? <FieldCharCount value={form.name} max={CAD_PARCEL_MAX_NAME} />
                      : null}
                  </Box>
                </Box>
                <TextField
                  label="Superficie"
                  size="small"
                  variant="outlined"
                  type="text"
                  fullWidth
                  value={form.area_m2}
                  disabled={fieldsLocked}
                  onChange={handleAreaChange}
                  inputProps={{
                    inputMode: 'decimal',
                    'aria-label': 'Superficie en metros cuadrados',
                  }}
                  InputProps={{
                    endAdornment: (
                      <Typography variant="caption" sx={{ color: colors.textSecondary, fontSize: '12px', ml: 0.5 }}>
                        m²
                      </Typography>
                    ),
                  }}
                  sx={showroomFieldSx}
                />
                {!listingCoreOnly ? (
                  <>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                      <FormControl size="small" disabled={fieldsLocked} fullWidth>
                        <InputLabel sx={showroomFormLabelSx} shrink>Orientación</InputLabel>
                        <Select
                          label="Orientación"
                          value={form.orientation}
                          onChange={handleChange('orientation')}
                          disabled={fieldsLocked}
                          variant="outlined"
                          notched
                          displayEmpty
                          renderValue={(selected) => getOrientationFormLabel(selected)}
                          sx={showroomSelectSx}
                          MenuProps={{ PaperProps: { sx: showroomMenuPaperSx } }}
                        >
                          <MenuItem value="" sx={showroomMenuItemSx}>{ORIENTATION_EMPTY_LABEL}</MenuItem>
                          {buildOrientationSelectOptions(form.orientation).map((option) => (
                            <MenuItem key={option.value} value={option.value} sx={showroomMenuItemSx}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <TextField
                        label="Posición"
                        size="small"
                        variant="outlined"
                        value={form.position_label}
                        disabled={fieldsLocked}
                        onChange={handleChange('position_label')}
                        sx={showroomFieldSx}
                      />
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                      <TextField
                        label="FOS"
                        size="small"
                        variant="outlined"
                        value={form.fos}
                        disabled={fieldsLocked}
                        onChange={handleChange('fos')}
                        sx={showroomFieldSx}
                      />
                      <TextField
                        label="FOT"
                        size="small"
                        variant="outlined"
                        value={form.fot}
                        disabled={fieldsLocked}
                        onChange={handleChange('fot')}
                        sx={showroomFieldSx}
                      />
                    </Box>
                  </>
                ) : null}
              </Box>

              <Box sx={showroomFormGroupDividerSx}>
                <Typography sx={showroomFormGroupTitleSx}>Presentación</Typography>
                <Box>
                  <TextField
                    label="Título comercial"
                    size="small"
                    fullWidth
                    variant="outlined"
                    value={form.title}
                    disabled={fieldsLocked}
                    inputProps={{ maxLength: CAD_PARCEL_MAX_TITLE }}
                    onChange={handleChange('title')}
                    sx={showroomFieldSx}
                  />
                  {editing ? <FieldCharCount value={form.title} max={CAD_PARCEL_MAX_TITLE} /> : null}
                </Box>
                <Box>
                  <TextField
                    label="Descripción"
                    size="small"
                    fullWidth
                    variant="outlined"
                    multiline
                    minRows={3}
                    value={form.description}
                    disabled={fieldsLocked}
                    inputProps={{ maxLength: CAD_PARCEL_MAX_DESC }}
                    onChange={handleChange('description')}
                    sx={showroomFieldSx}
                  />
                  {editing ? <FieldCharCount value={form.description} max={CAD_PARCEL_MAX_DESC} /> : null}
                </Box>
              </Box>

              {/* listingCoreOnly en lectura: el grupo queda vacío (estado = badge). */}
              {(!listingCoreOnly || editing) ? (
              <Box sx={showroomFormGroupDividerSx}>
                <Typography sx={showroomFormGroupTitleSx}>Estado</Typography>
                {!listingCoreOnly ? (
                  <FormControl size="small" disabled={fieldsLocked} fullWidth sx={{ mb: 1 }}>
                    <InputLabel sx={showroomFormLabelSx} shrink>Tipo</InputLabel>
                    <Select
                      label="Tipo"
                      value={form.cad_kind}
                      onChange={handleKindChange}
                      disabled={fieldsLocked}
                      variant="outlined"
                      notched
                      sx={showroomSelectSx}
                      MenuProps={{ PaperProps: { sx: showroomMenuPaperSx } }}
                    >
                      {CAD_KINDS.map((kind) => (
                        <MenuItem key={kind} value={kind} sx={showroomMenuItemSx}>
                          {CAD_KIND_LABELS[kind]}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : null}
                {listingCoreOnly ? (
                  editing ? (
                  <FormControl size="small" disabled={fieldsLocked} fullWidth>
                    <InputLabel sx={showroomFormLabelSx} shrink>Estado</InputLabel>
                    <Select
                      label="Estado"
                      value={form.stock_status}
                      onChange={handleChange('stock_status')}
                      disabled={fieldsLocked}
                      variant="outlined"
                      notched
                      sx={showroomSelectSx}
                      MenuProps={{ PaperProps: { sx: showroomMenuPaperSx } }}
                    >
                      {CAD_STOCK_STATUSES.map((status) => (
                        <MenuItem key={status} value={status} sx={showroomMenuItemSx}>
                          {CAD_STOCK_LABELS[status]}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  ) : null
                ) : (
                <Box sx={{ display: 'grid', gridTemplateColumns: editing ? '1fr 1fr' : '1fr', gap: 1 }}>
                  {editing ? (
                  <FormControl size="small" disabled={inventoryLocked}>
                    <InputLabel sx={showroomFormLabelSx} shrink>Estado</InputLabel>
                    <Select
                      label="Estado"
                      value={form.stock_status}
                      onChange={handleChange('stock_status')}
                      disabled={inventoryLocked}
                      variant="outlined"
                      notched
                      sx={showroomSelectSx}
                      MenuProps={{ PaperProps: { sx: showroomMenuPaperSx } }}
                    >
                      {CAD_STOCK_STATUSES.map((status) => (
                        <MenuItem key={status} value={status} sx={showroomMenuItemSx}>
                          {CAD_STOCK_LABELS[status]}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  ) : null}
                  <FormControl size="small" disabled={inventoryLocked}>
                    <InputLabel sx={showroomFormLabelSx} shrink>Uso</InputLabel>
                    <Select
                      label="Uso"
                      value={form.use}
                      onChange={handleChange('use')}
                      disabled={inventoryLocked}
                      variant="outlined"
                      notched
                      sx={showroomSelectSx}
                      MenuProps={{ PaperProps: { sx: showroomMenuPaperSx } }}
                    >
                      <MenuItem value="" sx={showroomMenuItemSx}>Sin definir</MenuItem>
                      {CAD_USES.map((use) => (
                        <MenuItem key={use} value={use} sx={showroomMenuItemSx}>
                          {CAD_USE_LABELS[use]}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                )}
              </Box>
              ) : null}

              <Box sx={showroomFormGroupDividerSx}>
                <Typography sx={showroomFormGroupTitleSx}>Precio</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    label="Precio"
                    size="small"
                    variant="outlined"
                    type="text"
                    value={formatPriceInputDisplay(form.price)}
                    disabled={fieldsLocked}
                    onChange={handlePriceChange}
                    inputProps={{ inputMode: 'numeric', 'aria-label': 'Precio' }}
                    InputProps={{
                      startAdornment: (
                        <Typography variant="caption" sx={{ color: colors.textSecondary, fontSize: '12px', mr: 0.5 }}>
                          $
                        </Typography>
                      ),
                    }}
                    sx={{ ...showroomFieldSx, flex: '1.5 1 140px', minWidth: 0 }}
                  />
                  <TextField
                    label="Moneda"
                    size="small"
                    variant="outlined"
                    value={form.currency}
                    disabled={fieldsLocked}
                    onChange={handleChange('currency')}
                    sx={{ ...showroomFieldSx, flex: '1 1 100px', minWidth: 0 }}
                  />
                </Box>
              </Box>

              {/* «Precio vs. mercado» debajo del precio, solo en lectura de un lote vendible. */}
              {!editing && isCadSellableLot(selected) && renderMarketComparison
                ? renderMarketComparison({
                  onEdit: showLotActions && !saving && !deleting ? startEdit : undefined,
                  revision: marketRevisionOf(selected),
                })
                : null}

              {!listingCoreOnly ? (
              <Box sx={showroomFormGroupDividerSx}>
                <Typography sx={showroomFormGroupTitleSx}>Fotos</Typography>
                <CadParcelImages
                  parcelId={selectedId}
                  canEdit={editing && !saving && !deleting}
                  initialImages={selected?.properties?.photos}
                  coverImage={selected?.properties?.cover_image}
                  onImagesChange={(patch) => {
                    if (selectedId) onPhotosChange?.(selectedId, patch);
                  }}
                />
              </Box>
              ) : null}

              {!listingCoreOnly ? (
              <Box sx={showroomFormGroupDividerSx}>
                <Typography sx={showroomFormGroupTitleSx}>Videos</Typography>
                <CadParcelVideos
                  parcelId={selectedId}
                  canEdit={editing && !saving && !deleting}
                  initialVideos={selected?.properties?.videos}
                  onVideosChange={(next) => {
                    if (selectedId) onVideosChange?.(selectedId, next);
                  }}
                />
              </Box>
              ) : null}

              {!listingCoreOnly ? (
              <Box sx={showroomFormGroupDividerSx}>
                <Typography sx={showroomFormGroupTitleSx}>Adjuntos</Typography>
                <CadParcelAttachments
                  parcelId={selectedId}
                  canEdit={editing && !saving && !deleting}
                  initialAttachments={selected?.properties?.attachments}
                  onAttachmentsChange={(next) => {
                    if (selectedId) onAttachmentsChange?.(selectedId, next);
                  }}
                />
              </Box>
              ) : null}
            </Box>
            <ShowroomParcelPlanimetria feature={selected} />
          </Box>
        ) : visible.length === 0 ? (
          <ShowroomParcelListEmpty
            hasActiveFilter={Boolean(
              String(parcelSearch || '').trim() || normalizeCadStatusFilter(statusFilter),
            )}
          />
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pt: 1.5 }}>
            {visible.map((feature) => {
              const id = getCadParcelId(feature);
              const props = feature.properties || {};
              return (
                <Box
                  key={id}
                  sx={showroomCardSx}
                  onClick={() => onSelect(id)}
                >
                  <CadParcelListHeading feature={feature} />
                  <CadParcelListMeta
                    props={props}
                    inquiryTotal={inquiryByItem
                      ? (getCollectionInquiryItemCounts(inquiryByItem, id)?.total || 0)
                      : null}
                  />
                </Box>
              );
            })}
          </Box>
        );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
      {toolbar}
      {detailChrome}
      <Box ref={setScrollNode} sx={{ ...showroomScrollSx, ...getScrollEdgeFadeSx(fadeTop, fadeBottom) }}>
        {body}
      </Box>
      {selected && editing ? (
        <Box sx={{ ...showroomFormFooterSx, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            disabled={saving || deleting}
            onClick={cancelEdit}
            sx={FORM_SECONDARY_BUTTON_SX}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon sx={{ fontSize: '18px !important' }} />}
            disabled={saving || deleting}
            onClick={handleSave}
            sx={FORM_PRIMARY_BUTTON_SX}
          >
            {saving ? 'Guardando…' : 'Guardar'}
          </Button>
        </Box>
      ) : null}
    </Box>
  );
}
